import { useEffect, useRef, useState } from "react";

/* La ruta Buenos Aires → Ushuaia que se dibuja mientras la app carga.
 *
 * Portado del prototipo "FiBOT Login a Carga". Los números —tiempos, tramos,
 * escala— son los del diseño; cambiarlos acá lo desalinea del original.
 *
 * POR QUÉ NO ES UNA BARRA DE PROGRESO COMÚN. El recorrido llega al 92 % en
 * 4,2 s y después avanza muy lento hasta 98,5 %. Nunca dice 100 % antes de que
 * la sesión esté lista de verdad: una barra que llega al final y se queda ahí
 * es la forma más rápida de que alguien concluya que se colgó.
 *
 * d3 y topojson se cargan del CDN, igual que en el prototipo, pero recién acá
 * y no en el index.html: son ~300 KB que sólo hacen falta en esta pantalla y
 * no tienen por qué pesar en el resto de la app. Mismas URLs, mismas versiones
 * y mismos hashes de integridad que el diseño.
 */

const RUTA = [
  [-58.38, -34.6], [-62.27, -38.72], [-62.99, -40.81], [-65.03, -42.77],
  [-67.5, -45.86], [-69.22, -51.62], [-67.7, -53.79], [-68.3, -54.8],
];

const W = 322, H = 352, PAD = 16, SW = 1.6, ESCALA_UNIDAD = 1.15;
const MS_PRINCIPAL = 4200, TOPE_PRINCIPAL = 0.92;
const MS_LENTO = 24000, TOPE_LENTO = 0.985;
const MS_FINAL = 520;

const LIBRERIAS = [
  {
    src: "https://unpkg.com/d3@7.9.0/dist/d3.min.js",
    integrity: "sha384-CjloA8y00+1SDAUkjs099PVfnY2KmDC2BZnws9kh8D/lX1s46w6EPhpXdqMfjK6i",
    global: "d3",
  },
  {
    src: "https://unpkg.com/topojson-client@3.1.0/dist/topojson-client.min.js",
    integrity: "sha384-Ukv1p/xTma6P4/2bY5KzWBw+ydSpXmhCMtyciIQVDJ1RmOxtCYNMF1uXT9T63H67",
    global: "topojson",
  },
];

const ATLAS = "https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json";

// Una sola carga aunque la pantalla se monte dos veces (el ingreso son dos
// pasos y entre uno y otro React puede remontar).
const cargarLibreria = ({ src, integrity, global }) =>
  new Promise((ok, falla) => {
    if (window[global]) return ok(window[global]);
    let el = document.querySelector(`script[src="${src}"]`);
    if (!el) {
      el = document.createElement("script");
      el.src = src;
      el.integrity = integrity;
      el.crossOrigin = "anonymous";
      document.head.appendChild(el);
    }
    el.addEventListener("load", () => ok(window[global]));
    el.addEventListener("error", falla);
  });

const suave = (t) => 1 - Math.pow(1 - t, 3);

const TransicionCarga = ({ dibujando, terminando, onFin, onPisoCumplido }) => {
  const svgRef = useRef(null);
  const rigRef = useRef(null);
  const rafRef = useRef(null);
  const avanceRef = useRef(0);
  const pisoAvisado = useRef(false);
  const [texto, setTexto] = useState("Trazando ruta");

  // ---- el mapa se arma una sola vez, cuando arranca el trazado ----
  useEffect(() => {
    if (!dibujando || rigRef.current) return;
    let vivo = true;

    (async () => {
      const [d3, topojson] = await Promise.all(LIBRERIAS.map(cargarLibreria));
      const topo = await fetch(ATLAS).then((r) => r.json());
      if (!vivo || !svgRef.current) return;

      const arg = topojson
        .feature(topo, topo.objects.countries)
        .features.find((f) => f.properties && f.properties.name === "Argentina");

      const svg = d3.select(svgRef.current);
      const proy = d3.geoMercator().fitExtent([[PAD, PAD], [W - PAD, H - PAD]], arg);
      const trazo = d3.geoPath(proy);

      svg.append("path").datum(arg).attr("d", trazo)
        .attr("fill", "#333a4a").attr("stroke", "#4b5262").attr("stroke-width", SW);

      const d = trazo({ type: "Feature", geometry: { type: "LineString", coordinates: RUTA } });

      svg.append("path").attr("d", d).attr("fill", "none").attr("stroke", "#8b929f")
        .attr("stroke-width", SW * 1.1)
        .attr("stroke-dasharray", `${SW * 2.5} ${SW * 2.5}`)
        .attr("stroke-linecap", "round");

      const hecho = svg.append("path").attr("d", d).attr("fill", "none")
        .attr("stroke", "#f5871f").attr("stroke-width", SW * 1.7)
        .attr("stroke-linecap", "round");

      const a = proy(RUTA[0]);
      const b = proy(RUTA[RUTA.length - 1]);
      [[a, "BUENOS AIRES"], [b, "USHUAIA"]].forEach(([p, nombre]) => {
        svg.append("circle").attr("cx", p[0]).attr("cy", p[1]).attr("r", SW * 2.1)
          .attr("fill", "#20262f").attr("stroke", "#f5871f").attr("stroke-width", SW);
        svg.append("text").attr("x", p[0] + 11).attr("y", p[1] + 4)
          .attr("font-family", "'JetBrains Mono',monospace").attr("font-size", 8.5)
          .attr("letter-spacing", 1.4).attr("fill", "#8d939d").text(nombre);
      });

      const unidad = svg.append("g");
      unidad.html(`<g transform="scale(${ESCALA_UNIDAD})">
        <rect x="-4" y="1.5" width="8" height="9.5" rx="1" fill="#f2f4f6" stroke="#20262f" stroke-width=".6"></rect>
        <rect x="-4" y="-8.5" width="8" height="9.5" rx="1" fill="#f2f4f6" stroke="#20262f" stroke-width=".6"></rect>
        <rect x="-4" y="-15.2" width="8" height="6.2" rx="1.4" fill="#f5871f" stroke="#20262f" stroke-width=".6"></rect>
        <rect x="-2.4" y="-14.4" width="4.8" height="2.1" rx=".7" fill="#b9c6d6"></rect>
        <g fill="#cfd6e0">
          <rect x="-5.6" y="-13.4" width="1.9" height="2.6" rx=".6"></rect><rect x="3.7" y="-13.4" width="1.9" height="2.6" rx=".6"></rect>
          <rect x="-5.6" y="-3.4" width="1.9" height="3" rx=".6"></rect><rect x="3.7" y="-3.4" width="1.9" height="3" rx=".6"></rect>
          <rect x="-5.6" y="6.8" width="1.9" height="3" rx=".6"></rect><rect x="3.7" y="6.8" width="1.9" height="3" rx=".6"></rect>
        </g></g>`);

      const nodo = hecho.node();
      const largo = nodo.getTotalLength();
      hecho.attr("stroke-dasharray", largo);
      rigRef.current = { nodo, hecho, unidad, largo };
      pintar(0);
    })().catch(() => {
      // Sin red no hay mapa, pero la transición y el ingreso siguen andando.
      setTexto("Cargando");
    });

    return () => { vivo = false; };
  }, [dibujando]);

  const pintar = (t) => {
    avanceRef.current = t;
    const rig = rigRef.current;
    if (!rig) return;
    rig.hecho.attr("stroke-dashoffset", rig.largo * (1 - t));
    const l = rig.largo * t;
    const p = rig.nodo.getPointAtLength(l);
    const haciaAdelante = l + 2 < rig.largo;
    const desde = haciaAdelante ? p : rig.nodo.getPointAtLength(Math.max(0, l - 2));
    const hasta = haciaAdelante ? rig.nodo.getPointAtLength(l + 2) : p;
    const ang = (Math.atan2(hasta.y - desde.y, hasta.x - desde.x) * 180) / Math.PI + 90;
    rig.unidad.attr("transform", `translate(${p.x} ${p.y}) rotate(${ang})`);
    setTexto(t >= 1 ? "Ruta lista" : `Trazando ruta · ${Math.round(t * 100)}%`);
  };

  // ---- el avance ----
  useEffect(() => {
    if (!dibujando) return undefined;
    let t0 = 0;
    const desde = terminando ? avanceRef.current : 0;
    if (!terminando) pisoAvisado.current = false;

    const paso = (ahora) => {
      if (!t0) t0 = ahora;
      const e = ahora - t0;
      if (terminando) {
        const t = Math.min(1, e / MS_FINAL);
        pintar(desde + (1 - desde) * suave(t));
        if (t >= 1) return;
      } else if (e < MS_PRINCIPAL) {
        pintar(suave(e / MS_PRINCIPAL) * TOPE_PRINCIPAL);
      } else if (!pisoAvisado.current) {
        /* El tramo principal se completa SIEMPRE. Si la app termina de cargar
           en un segundo, el mapa igual se dibuja entero: cortarlo a la mitad
           deja un tironcito raro y, sobre todo, desperdicia el unico momento
           en que se puede precargar sin molestar a nadie. */
        pisoAvisado.current = true;
        if (onPisoCumplido) onPisoCumplido();
        pintar(TOPE_PRINCIPAL);
      } else {
        const t = Math.min(1, (e - MS_PRINCIPAL) / MS_LENTO);
        pintar(TOPE_PRINCIPAL + (TOPE_LENTO - TOPE_PRINCIPAL) * t);
      }
      rafRef.current = requestAnimationFrame(paso);
    };

    rafRef.current = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(rafRef.current);
  }, [dibujando, terminando, onPisoCumplido]);

  /* El piso tampoco puede depender de requestAnimationFrame, por lo mismo que
     el fundido de abajo: en una pestaña en segundo plano rAF se congela. Y
     acá es peor que un overlay pegado — sin el aviso del piso nunca se decide
     entrar, así que quien mira otra pestaña mientras carga vuelve y encuentra
     el login para siempre, ya logueado y sin poder pasar.

     El temporizador avisa igual. Cuando rAF corre normal llega primero y este
     no hace nada: `pisoAvisado` lo deja pasar una sola vez. */
  useEffect(() => {
    if (!dibujando || terminando || !onPisoCumplido) return undefined;
    const id = setTimeout(() => {
      if (pisoAvisado.current) return;
      pisoAvisado.current = true;
      onPisoCumplido();
    }, MS_PRINCIPAL + 120);
    return () => clearTimeout(id);
  }, [dibujando, terminando, onPisoCumplido]);

  /* El fundido final NO puede depender de requestAnimationFrame: en una
     pestaña en segundo plano no corre, y el overlay quedaría tapando la app
     para siempre. Va por temporizador, que sí corre igual. */
  useEffect(() => {
    if (!terminando || !onFin) return undefined;
    const id = setTimeout(onFin, MS_FINAL + 420);
    return () => clearTimeout(id);
  }, [terminando, onFin]);

  return (
    <div className="fi-lockup">
      <div className="fi-mapa">
        <svg ref={svgRef} width={W} height={H} viewBox={`0 0 ${W} ${H}`}
             aria-label="Ruta Buenos Aires a Ushuaia" />
      </div>

      <div className="fi-camion">
        <div className="fi-rig">
          <div className="fi-ruta-piso"><div className="fi-raya" /></div>
          <div className="fi-viento" style={{ left: 26, bottom: 56, width: 34, animationDelay: ".05s" }} />
          <div className="fi-viento" style={{ left: 14, bottom: 44, width: 26, animationDelay: ".28s" }} />
          <div className="fi-viento" style={{ left: 32, bottom: 32, width: 30, animationDelay: ".44s" }} />
          <Bitren />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <div className="fi-word">
          <span style={{ color: "#fff" }}>Fi</span>
          <span style={{ color: "#f5871f" }}>BOT</span>
        </div>
        <div className="fi-tagline">Gestión de flotas</div>
        <div className="fi-estado">{texto}</div>
      </div>
    </div>
  );
};

/* El bitren: dos furgones y el tractor, tal cual el diseño. Va aparte para no
   enterrar la lógica de arriba abajo de setenta líneas de SVG. */
const RUEDAS = [14, 27, 88, 101, 168, 181, 204];

const Bitren = () => (
  <svg className="fi-bitren" width="264" height="73" viewBox="0 0 232 64" fill="none" aria-label="Bitren FiBOT">
    {[4, 78].map((x) => (
      <g key={x}>
        <rect x={x} y="10" width="70" height="34" rx="2" fill="#f2f4f6" />
        <rect x={x} y="10" width="70" height="3.4" rx="1.7" fill="#d9dee4" />
        <rect x={x} y="40.6" width="70" height="3.4" fill="#c6ccd4" />
        <g stroke="#dde1e6" strokeWidth="1">
          {[12, 24, 36, 48, 60].map((d) => <path key={d} d={`M${x + d} 14 V40`} />)}
        </g>
        <text x={x + 18} y="30" fontFamily="'Space Grotesk',Helvetica,sans-serif"
              fontSize="12" fontWeight="700" letterSpacing="-.4">
          <tspan fill="#253343">Fi</tspan><tspan fill="#f5871f">BOT</tspan>
        </text>
        <rect x={x + 2} y="44" width="66" height="3" fill="#6a7280" />
      </g>
    ))}

    <rect x="72" y="44.5" width="8" height="2.6" rx="1.3" fill="#6a7280" />
    <circle cx="76" cy="45.8" r="1.9" fill="#aeb7c4" />

    <rect x="150" y="40" width="64" height="5" rx="1.5" fill="#6a7280" />
    <rect x="157" y="6" width="3.4" height="34" rx="1.7" fill="#9aa5b4" />
    <rect x="160" y="14" width="19" height="28" rx="3" fill="#e07b13" />
    <rect x="163.5" y="18" width="9" height="9" rx="1.5" fill="#b9c6d6" />
    <path d="M179 13 V42" stroke="#b8650c" strokeWidth="1.4" />
    <path d="M196 30 V42" stroke="#c96f0f" strokeWidth="1.4" />
    <rect x="187" y="32" width="7" height="2.6" rx="1.3" fill="#c96f0f" />
    <rect x="176" y="12" width="34" height="30" rx="4" fill="#f5871f" />
    <path d="M181 15.5 H205.5 V28 H179 Z" fill="#b9c6d6" />
    <path d="M181 15.5 L179 28 H184 L186 15.5 Z" fill="#cdd8e4" />
    <rect x="160" y="8" width="48" height="5" rx="2.5" fill="#e07b13" />
    <path d="M207 18 L215 14.5" stroke="#f2f4f6" strokeWidth="1.6" />
    <rect x="213" y="12" width="4" height="5" rx="1" fill="#f2f4f6" />
    <rect x="204" y="30" width="6" height="12" rx="1.5" fill="#a95d0c" />
    <rect x="203" y="41" width="12" height="3.4" rx="1.4" fill="#cfd6e0" />
    <rect x="205" y="34.5" width="6" height="4" rx="1" fill="#ffe6bd" />
    <rect x="162" y="43" width="16" height="8" rx="3.5" fill="#aeb7c4" />

    {RUEDAS.map((cx) => (
      <g className="fi-rueda" key={cx}>
        <circle cx={cx} cy="48" r="8" fill="#4f5867" stroke="#8f99a8" strokeWidth="1.2" />
        <circle cx={cx} cy="48" r="3.6" fill="#cfd6e0" />
        <circle cx={cx} cy="48" r="1.4" fill="#6a7280" />
        <path d={`M${cx} 41.5 V44 M${cx} 52 V54.5 M${cx - 6.5} 48 H${cx - 4} M${cx + 4} 48 H${cx + 6.5}`}
              stroke="#8f99a8" strokeWidth="1.1" />
      </g>
    ))}
  </svg>
);

export default TransicionCarga;
